import React, { useState } from 'react';
import { message } from 'antd';
import { useParams } from 'react-router-dom';
import Loader from 'components/commonComponents/Loader/Index';

// assets
import DropDown from 'assets/customSVG/accounts/DropDown';
import { ICONS } from 'assets/icons';

// hooks
import useJsonToExcel from 'hooks/useJsonToExcel';

// api
import { getSequenceNodeMetrics } from '../../../api/sequence';

// helpers
import { cn } from '../../../../../helper/cn';
import { formatNumber } from '../../../helpers';
import { useAspSelector } from '../../../../../test/jest-redux-hooks';

// export const formatNumber = (num) => num?.toLocaleString();

const Metrics = ({ metrics, type, nodeId }) => {
  const containerStyle = 'flex flex-col gap-1 justify-center items-center';
  const textStyle = 'text-[13px] weight-medium text-[var(--font-600)]';
  const countStyle = 'text-[15px] weight-semibold text-[var(--font-600)]';
  const dotStyle = 'size-2.5 contents-[""] rounded-full';
  const line =
    'contents-[""] border-[0.5px] border-[var(--border-100)] h-9 mx-1';

  const { handleDownloadExcel } = useJsonToExcel();
  const [isLoading, setIsLoading] = useState(false);
  const { sequence_id: sequenceId, campaign_id: campaignId } = useParams();
  const { currentBrand } = useAspSelector((state) => state.app);

  const formatMetricsData = (data) => {
    return data.map(item => ({
      'profile_name': item.profile_name,
      'node_order': item.node_order,
      'phone_number': item.phone_number,
      'country_code': item.country_code,
      'status': item.status,
    }));
  };

  const handleExport = async () => {
    if (!sequenceId || !nodeId) {
      message.error('Unable to export: Missing sequence or node ID');
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        brand_id: currentBrand?.brand_id,
        campaign_id: campaignId,
      };
      const response = await getSequenceNodeMetrics(sequenceId, nodeId, payload);
      const metricsData = response?.data?.data || [];
      
      if (metricsData.length === 0) {
        message.info('No metrics data available to export');
        return;
      }

      const formattedData = formatMetricsData(metricsData);
      
      // Define column mappings for better Excel headers
      const columnMappings = [
        { from: 'profile_name', to: 'Profile Name' },
        { from: 'phone_number', to: 'Phone Number' },
        { from: 'country_code', to: 'Country Code' },
        { from: 'status', to: 'Status' },
      ];

      // Generate filename with current date and node ID
      const fileName = `node_${nodeId}_metrics_${new Date().toISOString().split('T')[0]}`;
      
      // Call the export function
      handleDownloadExcel({
        fileName,
        data: formattedData,
        keys: columnMappings
      });
      
      // message.success('Metrics exported successfully');
    } catch (error) {
      console.error('Error exporting metrics:', error);
      message.error('Failed to export metrics. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='w-full h-[4.375rem] relative flex items-center gap-5 bg-white px-5 py-2.5 rounded-2xl shadow-[0px_3.1px_23.21px_0px_#2D30361A]'>
      {type === 'TRIGGER' ? (
        <div className={cn(containerStyle)}>
          <p className={cn(countStyle)}>
            {(metrics?.triggered_count &&
              formatNumber(metrics?.triggered_count)) ||
              0}
          </p>
          <p className='flex items-center gap-2.5'>
            <span className={cn(dotStyle, 'bg-[#FF543E]')}></span>
            <span className={cn(textStyle)}>Triggered</span>
          </p>
        </div>
      ) : (
        <>
          <div className={cn(containerStyle)}>
            <p className={cn(countStyle)}>
              {(metrics?.sent_count && formatNumber(metrics?.sent_count)) || 0}
            </p>
            <p className='flex items-center gap-2.5'>
              <span className={cn(dotStyle, 'bg-[#566F8F]')}></span>
              <span className={cn(textStyle)}>Sent</span>
            </p>
          </div>
          <div className={cn(line)}></div>
          <div className={cn(containerStyle)}>
            <p className={cn(countStyle)}>
              {(metrics?.delivered_count &&
                formatNumber(metrics?.delivered_count)) ||
                0}
            </p>
            <p className='flex items-center gap-2.5'>
              <span className={cn(dotStyle, 'bg-[#04B8A2]')}></span>
              <span className={cn(textStyle)}>Delivered</span>
            </p>
          </div>

          <div className={cn(line)}></div>
          <div className={cn(containerStyle)}>
            <p className={cn(countStyle)}>
              {(metrics?.abandoned_count &&
                formatNumber(metrics?.abandoned_count)) ||
                0}
            </p>
            <p className='flex items-center gap-2.5'>
              <span className={cn(dotStyle, 'bg-[#FF543E]')}></span>
              <span className={cn(textStyle)}>Abandoned</span>
            </p>
          </div>
        </>
      )}

      <div className={cn('', line)}></div>
      <div className={cn(containerStyle)}>
        <p className={cn(countStyle)}>
          {(metrics?.failed_count && formatNumber(metrics?.failed_count)) || 0}
        </p>
        <p className='flex items-center gap-2.5'>
          <span className={cn(dotStyle, 'bg-[#F6A723]')}></span>
          <span className={cn(textStyle)}>Failure</span>
        </p>
      </div>

      <div className={cn(line)}></div>
      <div className='flex items-center gap-2'>
        <div className='absolute -bottom-[1.125rem] left-1/2 -translate-x-1/2'>
          <DropDown
            width={34}
            height={32}
            color={'white'}
          />
        </div>
        <div 
          className={cn(containerStyle, 'cursor-pointer gap-0', { 'opacity-70': isLoading })}
          onClick={!isLoading ? handleExport : undefined}
          title='Export to Excel'
        >
          <p className={cn(countStyle, 'mb-1.5')}>
            {isLoading ? (
              <Loader Width={16} Height={16} loaderBg='var(--BG-100)' loaderColor='var(--new-primary)' />
            ) : (
              <img src={ICONS.Download} alt='Export' className='w-4 h-4' />
            )}
          </p>
          <p className='flex items-center'>
            <span className={cn(textStyle)}>{isLoading ? 'Exporting...' : 'Export'}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Metrics;
